import { CalculationResults } from '../types';
import { logger } from './logging';

const MIN_LOADING_TIME = 4000;
const API_URL = 'https://hoacalcapi3-eeef897c5864.herokuapp.com/calculate';
const DEFAULT_INVESTMENT_TERM_MONTHS = 120;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const RETRY_BACKOFF_FACTOR = 1.5;

// Event bus for API calls
type ApiCallListener = (call: ApiCall) => void;
const listeners: ApiCallListener[] = [];

export interface ApiCall {
  id: string;
  timestamp: number;
  url: string;
  params: Record<string, string>;
  status: 'pending' | 'success' | 'error';
  response?: any;
  error?: string;
}

export function subscribeToApiCalls(listener: ApiCallListener) {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
}

function generateCallId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

function notifyListeners(call: Omit<ApiCall, 'id'>) {
  const callWithId = {
    id: generateCallId(),
    ...call
  };
  listeners.forEach(listener => listener(callWithId));
}

async function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function makeApiCall(params: Record<string, string>): Promise<CalculationResults> {
  const url = new URL(API_URL);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const requestId = `req_${Date.now()}_${attempt}`;
      
      logger.info(`Starting API request [${requestId}]`, {
        url: url.toString(),
        params,
        attempt
      });

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      // Log raw response
      const rawText = await response.text();
      logger.info(`Raw response [${requestId}]`, {
        status: response.status,
        text: rawText.substring(0, 1000) // First 1000 chars
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // Parse the response
      const data = rawText ? JSON.parse(rawText) : null;
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format: expected object');
      }

      // Validate the response structure
      if (!Array.isArray(data.dates) || 
          !Array.isArray(data.risk_free) || 
          !Array.isArray(data.low_risk) || 
          !Array.isArray(data.moderate_risk) || 
          !Array.isArray(data.high_risk)) {
        throw new Error('Invalid response format: missing required arrays');
      }

      // Validate array lengths match
      const length = data.dates.length;
      if (data.risk_free.length !== length ||
          data.low_risk.length !== length ||
          data.moderate_risk.length !== length ||
          data.high_risk.length !== length) {
        throw new Error('Invalid response format: array lengths do not match');
      }

      // Return the validated data
      const result: CalculationResults = {
        dates: data.dates,
        risk_free: data.risk_free,
        low_risk: data.low_risk,
        moderate_risk: data.moderate_risk,
        high_risk: data.high_risk
      };

      logger.info(`API call successful [${requestId}]`, { 
        dataPoints: result.dates.length,
        dateRange: {
          start: result.dates[0],
          end: result.dates[result.dates.length - 1]
        }
      });

      return result;

    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      logger.error(`API call failed (attempt ${attempt}/${MAX_RETRIES})`, lastError);

      if (attempt < MAX_RETRIES) {
        const delay = RETRY_DELAY * Math.pow(RETRY_BACKOFF_FACTOR, attempt - 1);
        await wait(delay);
      }
    }
  }

  throw lastError || new Error('Failed to fetch data after all retry attempts');
}

const getDevMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('devMode') === 'true';
};

const getMockDataPreference = (): boolean => {
  const pref = localStorage.getItem('useMockData');
  return pref !== null ? JSON.parse(pref) : true;
};

export const isDevMode = (): boolean => {
  return getDevMode();
};

export const useMockData = (): boolean => {
  return getDevMode() && getMockDataPreference();
};

export const setMockDataPreference = (useMock: boolean): void => {
  localStorage.setItem('useMockData', JSON.stringify(useMock));
};

export async function calculateScenarios(
  currentReserveFund: number,
  annualDuesAndFees: number,
  annualExpenses: number,
  recentInvestmentReturns: number,
  timeRangeYears: number = 5
): Promise<CalculationResults> {
  const devMode = isDevMode();
  const shouldUseMockData = useMockData();
  
  try {
    const params = {
      current_reserve_fund: currentReserveFund.toString(),
      annual_dues_and_fees: annualDuesAndFees.toString(),
      annual_expenses: annualExpenses.toString(),
      recent_investment_returns: recentInvestmentReturns.toString(),
      investment_term_months: DEFAULT_INVESTMENT_TERM_MONTHS.toString()
    };

    logger.info('Starting calculation', { params, useMockData: shouldUseMockData });

    const apiCall = {
      timestamp: Date.now(),
      url: API_URL,
      params,
      status: 'pending' as const
    };
    notifyListeners(apiCall);

    const loadingPromise = new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME));

    try {
      let data: CalculationResults;

      if (shouldUseMockData) {
        logger.info('Using mock data');
        data = mockCalculateScenarios(
          currentReserveFund,
          annualDuesAndFees,
          annualExpenses,
          recentInvestmentReturns
        );
      } else {
        logger.info('Making live API call');
        data = await makeApiCall(params);
      }

      // Wait for minimum loading time
      await loadingPromise;

      notifyListeners({
        ...apiCall,
        status: 'success',
        response: data
      });

      return data;
    } catch (error) {
      const apiError = error instanceof Error ? error : new Error('Unknown API error');
      
      logger.error('API call failed', apiError);
      
      notifyListeners({
        ...apiCall,
        status: 'error',
        error: apiError.message
      });

      // Wait for minimum loading time even on error
      await loadingPromise;

      if (devMode && !shouldUseMockData) {
        logger.warn('Falling back to mock data');
        const mockData = mockCalculateScenarios(
          currentReserveFund,
          annualDuesAndFees,
          annualExpenses,
          recentInvestmentReturns
        );

        notifyListeners({
          ...apiCall,
          status: 'success',
          response: mockData
        });

        return mockData;
      }

      throw apiError;
    }
  } catch (error) {
    const calculationError = error instanceof Error ? error : new Error('An unexpected error occurred');
    logger.error('Calculation failed', calculationError);
    throw calculationError;
  }
}